/**
 * Section Components for NCR Edit
 */

import { useRef, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { FileText, MessageSquare, Clock, Upload, Trash2, Loader2 } from 'lucide-react';
import { NCRDocument, NCRComment, NCRActionLog, qualityAPI } from '../../lib/api';
import { toast } from 'sonner';
import { useFormatters } from '../../contexts/PreferencesContext';

const ACCEPTED_FILE_TYPES = '.png,.jpg,.jpeg,.tif,.bmp,.gif,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.pdf,.svg,.mp4,.msg';
const MAX_FILE_SIZE_MB = 10;

interface DocumentsSectionProps {
  ncrId: number;
  documents: NCRDocument[];
  onDocumentsChanged: () => void;
}

export function DocumentsSection({ ncrId, documents, onDocumentsChanged }: DocumentsSectionProps) {
  const { formatDate } = useFormatters();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [dragOver, setDragOver] = useState(false);

  const handleUpload = async (files: FileList | null) => {
    if (!files || files.length === 0) return;

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
        toast.error(`File "${file.name}" exceeds ${MAX_FILE_SIZE_MB}MB limit`);
        continue;
      }

      try {
        setUploading(true);
        const formData = new FormData();
        formData.append('file', file);
        formData.append('ncr', String(ncrId));
        formData.append('file_name', file.name);
        formData.append('file_size', String(file.size));
        formData.append('file_type', file.type || 'application/octet-stream');
        await qualityAPI.uploadNCRDocument(formData);
        toast.success(`Uploaded "${file.name}"`);
      } catch (error) {
        console.error('Error uploading document:', error);
        toast.error(`Failed to upload "${file.name}"`);
      } finally {
        setUploading(false);
      }
    }
    onDocumentsChanged();
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleDelete = async (docId: number, fileName: string) => {
    try {
      setDeletingId(docId);
      await qualityAPI.deleteNCRDocument(docId);
      toast.success(`Deleted "${fileName}"`);
      onDocumentsChanged();
    } catch (error) {
      console.error('Error deleting document:', error);
      toast.error('Failed to delete document');
    } finally {
      setDeletingId(null);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    handleUpload(e.dataTransfer.files);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <FileText className="h-5 w-5" />
          Supporting Documents ({documents.length})
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Upload area */}
        <div
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
          className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
            dragOver ? 'border-primary bg-primary/5' : 'border-muted-foreground/25 hover:border-muted-foreground/50'
          }`}
        >
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept={ACCEPTED_FILE_TYPES}
            onChange={(e) => handleUpload(e.target.files)}
            className="hidden"
          />
          {uploading ? (
            <div className="flex items-center justify-center gap-2 text-muted-foreground">
              <Loader2 className="h-5 w-5 animate-spin" />
              <span>Uploading...</span>
            </div>
          ) : (
            <>
              <Upload className="h-8 w-8 mx-auto text-muted-foreground/50 mb-2" />
              <p className="text-sm text-muted-foreground mb-2">
                Drag and drop files here, or
              </p>
              <Button
                variant="outline"
                size="sm"
                onClick={() => fileInputRef.current?.click()}
              >
                Browse Files
              </Button>
              <p className="text-xs text-muted-foreground mt-2">
                Max {MAX_FILE_SIZE_MB}MB per file. Supported: images, documents, PDFs, videos
              </p>
            </>
          )}
        </div>

        {/* Document list */}
        {documents.length === 0 ? (
          <p className="text-muted-foreground text-center py-2">No documents attached yet</p>
        ) : (
          <div className="space-y-2">
            {documents.map((doc) => (
              <div key={doc.id} className="flex items-center justify-between p-3 border rounded">
                <div className="flex-1 min-w-0">
                  <p className="font-medium truncate">{doc.file_name}</p>
                  <p className="text-xs text-muted-foreground">
                    {doc.uploaded_by_details?.first_name
                      ? `${doc.uploaded_by_details.first_name} ${doc.uploaded_by_details.last_name || ''}`.trim()
                      : doc.uploaded_by_details?.username || 'Unknown'}
                    {' · '}
                    {formatDate(doc.uploaded_at)}
                    {' · '}
                    {(doc.file_size / 1024).toFixed(0)} KB
                  </p>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  className="text-destructive hover:text-destructive h-8 w-8"
                  onClick={() => handleDelete(doc.id, doc.file_name)}
                  disabled={deletingId === doc.id}
                >
                  {deletingId === doc.id ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Trash2 className="h-4 w-4" />
                  )}
                </Button>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

interface CommentsSectionProps {
  comments: NCRComment[];
  newComment: string;
  onNewCommentChange: (value: string) => void;
  onAddComment: () => void;
}

export function CommentsSection({
  comments,
  newComment,
  onNewCommentChange,
  onAddComment,
}: CommentsSectionProps) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <MessageSquare className="h-5 w-5" />
          Communications ({comments.length})
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {comments.map((comment) => (
            <div key={comment.id} className="border-l-4 border-blue-500 pl-4 py-2">
              <div className="flex items-center justify-between mb-1">
                <p className="font-medium">{comment.created_by_name}</p>
                <p className="text-xs text-muted-foreground">
                  {new Date(comment.created_at).toLocaleString()}
                </p>
              </div>
              <p className="text-sm">{comment.comment}</p>
            </div>
          ))}

          <div className="space-y-2">
            <Textarea
              value={newComment}
              onChange={(e) => onNewCommentChange(e.target.value)}
              placeholder="Add a comment..."
              rows={3}
            />
            <Button onClick={onAddComment} disabled={!newComment.trim()}>
              Add Comment
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

interface ActionLogSectionProps {
  actionLogs: NCRActionLog[];
}

export function ActionLogSection({ actionLogs }: ActionLogSectionProps) {
  const { formatDate } = useFormatters();
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Clock className="h-5 w-5" />
          Workflow Action Log ({actionLogs.length})
        </CardTitle>
        <p className="text-sm text-muted-foreground mt-1">
          Complete audit trail of all NCR activities and status changes
        </p>
      </CardHeader>
      <CardContent>
        {actionLogs.length === 0 ? (
          <p className="text-muted-foreground text-center py-4">No actions recorded yet</p>
        ) : (
          <div className="space-y-3">
            {actionLogs.map((log) => {
              const isStatusChange =
                log.action_type === 'status_changed' ||
                log.action_type === 'detail_submitted' ||
                log.action_type === 'rca_submitted' ||
                log.action_type === 'car_submitted' ||
                log.action_type === 'ncr_closed';

              return (
                <div
                  key={log.id}
                  className={`p-3 rounded-lg border-l-4 ${
                    isStatusChange
                      ? 'bg-blue-50 dark:bg-blue-900/10 border-blue-500'
                      : 'bg-gray-50 dark:bg-gray-800/50 border-gray-300 dark:border-gray-600'
                  }`}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <Badge
                          variant="outline"
                          className={`text-xs ${
                            isStatusChange
                              ? 'bg-blue-100 text-blue-700 border-blue-300 dark:bg-blue-900/20 dark:text-blue-400'
                              : 'bg-gray-100 text-gray-700 border-gray-300 dark:bg-gray-700 dark:text-gray-300'
                          }`}
                        >
                          {log.action_type_display}
                        </Badge>
                        {log.old_value && log.new_value && (
                          <span className="text-xs text-muted-foreground">
                            {log.old_value} → {log.new_value}
                          </span>
                        )}
                      </div>
                      <p className="text-sm mt-1.5">{log.action_description}</p>
                      <p className="text-xs text-muted-foreground mt-1">by {log.performed_by_name}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-muted-foreground whitespace-nowrap">
                        {formatDate(log.performed_at)}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {new Date(log.performed_at).toLocaleTimeString('en-IN', {
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

interface CloseNCRDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onClose: () => void;
}

export function CloseNCRDialog({ open, onOpenChange, onClose }: CloseNCRDialogProps) {
  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Close NCR</AlertDialogTitle>
          <AlertDialogDescription>
            Are you sure you want to close this NCR? This action will mark the non-conformance as
            resolved and complete the workflow.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction onClick={onClose}>Close NCR</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
